import useAlert from 'utils/alert';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getOrder } from '../services/querys';
import { useCallback, useState } from 'react';
import { AccountingInvolvedParty, AccountingType } from 'v2-types/order';
import useDeleteEntry from 'utils/ledger/use-delete-ledger-entry';
import { createLedgerLink, mutationEditEntry, mutationUpdatePosted } from '../services/mutations';
import { filterEntriesToDisbursment } from '../services';
import { createEntryForm, editEntryForm } from '../types';
import { deleteLedgerAssociation } from 'graphql/ledger/mutations';
import { useSocketAccountingEventInvalidator } from 'hooks/useSocketAccountingEventInvalidator';

type ConfirmDelateType = {
	openConfirm: boolean;
	callbackSuccess: (() => void) | null;
	title: string;
	confirmationMessage: string;
};

type ModalsType = {
	kind: string;
	idEntry?: string;
	entry?: AccountingType;
	partyEntry?: AccountingInvolvedParty;
};

const useDisbursments = (kindEntry: string[]) => {
  const showAlert = useAlert();
  const { t } = useTranslation();
  const { id: orderId } = useParams<{ id: string }>();
  const [accounting, setAccounting] = useState<AccountingType[]>([]);
  const [disbursments, setDisbursments] = useState<AccountingType[]>([]);
  const queryCache = useQueryClient();
  const [modals, setModals] = useState<ModalsType>({
    kind: '',
    idEntry: '',
  });
  const [confirmDelate, setConfirmDelate] = useState<ConfirmDelateType>({
    openConfirm: false,
    callbackSuccess: null,
    title: '',
    confirmationMessage: '',
  });
  const [itemsCollapsed, setItemsCollapsed] = useState<string[]>([]);

  useSocketAccountingEventInvalidator({ queries: ['accounting-order-disbursments', orderId] });

  const updateDisbursments = useCallback((entries: AccountingType[]) => {
    const { filteredDisbursments, accountingFiltered } = filterEntriesToDisbursment(
      entries,
      kindEntry
    );
    setAccounting(accountingFiltered);
    setDisbursments(filteredDisbursments);
  }, [kindEntry]);

  const { isLoading, isFetching } = useQuery(
    ['accounting-order-disbursments', orderId],
    () => getOrder(orderId),
    {
      enabled: !!orderId,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          updateDisbursments(data.getOrder.accounting);
        }
      },
    }
  );

  const handleModals = (
    modalName: string,
    idEntry?: string,
    entry?: AccountingType,
    partyEntry?: AccountingInvolvedParty
  ) => {
    setModals({
      kind: modalName,
      idEntry,
      entry,
      partyEntry,
    });
  };

  const { deleteEntry, deleteEntryResponse } = useDeleteEntry(
    (data) => {
      if (data) {
        queryCache.invalidateQueries({ queryKey: ['accounting-order-disbursments', orderId] });
        showAlert(t('dialogs:order-updated'), 'success');
        deleteEntryResponse.reset();
      }
    },
    () => { }
  );

  const handleDeleteEntry = (accountingId: string) => {
    deleteEntry({
      id: orderId,
      accountingId,
    });
  };

  const { mutate: createLedgerLinkMutation, ...createLedgerLinkResponse } = useMutation(
    createLedgerLink,
    {
      onSuccess: (data) => {
        if (data) {
          queryCache.invalidateQueries({ queryKey: ['accounting-order-disbursments', orderId] });
          showAlert(t('dialogs:order-updated'), 'success');
          createLedgerLinkResponse.reset();
        }
      },
    }
  );

  const { mutate: editLedgerEntryMutation, ...editLedgerEntryResponse } = useMutation(
    mutationEditEntry,
    {
      onSuccess: (data) => {
        if (data) {
          queryCache.invalidateQueries({ queryKey: ['accounting-order-disbursments', orderId] });
          showAlert(t('dialogs:order-updated'), 'success');
          editLedgerEntryResponse.reset();
        }
      },
    }
  );

  const { mutate: updatePostedMutation, ...updatePostedResponse } = useMutation(
    mutationUpdatePosted,
    {
      onSuccess: (data) => {
        if (data) {
          queryCache.invalidateQueries({ queryKey: ['accounting-order-disbursments', orderId] });
          showAlert(t('dialogs:order-updated'), 'success');
          editLedgerEntryResponse.reset();
        }
      },
    }
  );

  const { mutate: deleteAssociation, isLoading: isDeleteAssociationLoading } =
		useMutation(deleteLedgerAssociation);

  const handleConfirmDelete = (
    openConfirm: boolean,
    callback: (() => void) | null,
    title: string,
    confirmationMessage: string
  ) => {
    setConfirmDelate({
      openConfirm,
      title,
      confirmationMessage,
      callbackSuccess: () => {
        if (callback) {
          callback();
          handleConfirmDelete(false, null, '', '');
        }
      },
    });
  };

  const openDeleteAssociationModal = (entryId: string, party: AccountingInvolvedParty) => {
    handleConfirmDelete(
      true,
      () => {
        deleteAssociation({
          id: orderId,
          accountingId: entryId,
          kind: party.kind,
          partyId: party._id,
        });
      },
      'Delete Party Association',
      'Are you sure you want to delete the association?'
    );
  };

  const handleCreationEntry = (data: createEntryForm) => {
    const filteredTargets = data.targets.filter((target) => target.checked);

    const targetsWithoutChecked = filteredTargets.map(({ checked, ...target }) => target);

    const dataCreationEntry = {
      ...data,
      targets: targetsWithoutChecked,
      id: orderId,
    };

    createLedgerLinkMutation(dataCreationEntry);
  };

  const handleEditEntry = (data: editEntryForm, id: string) => {
    const dataEditEntry = {
      id: orderId,
      accountingId: id,
      data: {
        ...data,
      },
    };

    editLedgerEntryMutation(dataEditEntry);
  };

  const handleUpdatePosted = (id: string, posted: boolean) => {
    const data = {
      id: orderId,
      accountingId: id,
      posted,
    };

    updatePostedMutation(data);
  };

  return {
    disbursments,
    isLoading: isLoading || isFetching,
    handleModals,
    modals,
    handleDeleteEntry,
    accounting,
    confirmDelate,
    handleConfirmDelete,
    handleCreationEntry,
    handleEditEntry,
    handleUpdatePosted,
    isLoadingMutations:
			createLedgerLinkResponse.isLoading ||
			deleteEntryResponse.isLoading ||
			editLedgerEntryResponse.isLoading ||
			updatePostedResponse.isLoading ||
			isDeleteAssociationLoading,
    updateDisbursments,
    itemsCollapsed,
    setItemsCollapsed,
    openDeleteAssociationModal,
  };
};

export default useDisbursments;
